import React, { FC, useCallback, useEffect, useReducer } from 'react';

import { RuleCreate, RuleRead } from '@ariksa/compliance-policies/api';
import { Resource } from '@ariksa/inventory-core/api';
import { cloneDeep, find } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { Optional } from 'types/utils';

import { CustomPolicyDetails } from 'containers/PolicyHub/Policies/CreatePolicy/CustomPolicyDetails';
import { policyProviderOptions } from 'containers/PolicyHub/Policies/CreatePolicy/utils';
import { cleanUpObject, parseQuery } from 'containers/PolicyHub/Policies/utils';
import { policyHubSaga } from 'containers/PolicyHub/saga';
import { selectPolicyHub } from 'containers/PolicyHub/selectors';
import { actions } from 'containers/PolicyHub/slice';
import * as policyHubSlice from 'containers/PolicyHub/slice';
import { useInjector } from 'utils/inject';

interface IUpdatePolicy {}

export const UpdatePolicy: FC<IUpdatePolicy> = props => {
  useInjector(policyHubSlice.sliceKey, policyHubSlice.reducer, policyHubSaga);

  const { searchQuery, policyById, updatePolicy } = useSelector(
    selectPolicyHub,
  );

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams<{ policy_id: string }>();

  const [state, updateState] = useReducer(
    (prev, next) => {
      return { ...prev, ...next };
    },
    {
      alert_category: '',
      severity: '',
      category_class: '',
      provider: policyProviderOptions[0],
    },
  );

  // get custom query
  const getCustomQueryById = useCallback(
    queryId => {
      dispatch(
        actions.getQueryById({
          q: { id: queryId },
          onSuccess: res => {
            const query = parseQuery(
              res?.[0].wrapper ?? {},
              res?.[0].attributes ?? [],
            );
            dispatch(actions.setSearchQuery(query));
          },
        }),
      );
    },
    [dispatch],
  );

  // get policy
  const getPolicyById = useCallback(
    policyId => {
      dispatch(
        actions.getPolicyById({
          q: { policyId },
          onSuccess: (res: Optional<RuleRead>) => {
            //console.log(res);
            updateState({
              alert_category: res?.alert_category,
              category_class: res?.category_class,
              severity: res?.severity,
              provider:
                find(policyProviderOptions, o => o?.value === res?.provider) ??
                policyProviderOptions[0],
            });
            /*if (!!res?.provider)
              getQuerySuggestions(
                {
                  for_field: 'relationship_resources',
                  build_id: searchQuery.build.id,
                },
                res?.provider as CloudProvider,
              );*/
            getCustomQueryById(res?.search_id!);
          },
        }),
      );
    },
    [dispatch, getCustomQueryById],
  );

  useEffect(() => {
    getPolicyById(params.policy_id);
  }, [getPolicyById, params.policy_id]);

  const updateForm = useCallback((field, value) => {
    updateState({ [field]: value });
  }, []);

  const handleSubmit = data => {
    const { summary, description, category, severity, alertDescription } = data;
    const { alertCategory = {}, categoryClass = {} } = category ?? {};
    console.log('Save query');
    // first save the query

    const policy: RuleCreate = {
      config_id: summary,
      is_enabled: true,
      search_id: '',
      compliance_description: description,
      alert_description: alertDescription,
      alert_category: alertCategory.value,
      category_class: categoryClass.value,
      severity: severity.value,
      provider: state.provider?.value,
    };
    dispatch(
      actions.saveQuery({
        q: {
          searchWrapper: {
            id: policyById.data.search_id,
            wrapper: {
              resource: cleanUpObject(
                cloneDeep(searchQuery.resource),
              ) as Resource,
            },
            name: summary ?? '-',
          },
        },
        onSuccess: res => {
          if (!res?.id) {
            console.error('failed to save custom search query');
          }

          policy.search_id = res?.id;
          dispatch(
            actions.updatePolicy({
              q: { policyId: params.policy_id!, ruleCreate: policy },
              onSuccess: () => {
                // Back to the policies list; /policy-hub/policy is not a route.
                navigate('/policy-hub/policy/cloud');
              },
            }),
          );
        },
      }),
    );
  };

  return (
    <CustomPolicyDetails
      onSubmit={handleSubmit}
      action={'Update'}
      alert_description={policyById.data.alert_description}
      compliance_description={policyById.data.compliance_description}
      alert_category={state.alert_category}
      category_class={state.category_class}
      severity={state.severity}
      provider={state.provider}
      config_id={policyById.data.config_id}
      updateForm={updateForm}
      isLoading={updatePolicy.isLoading}
    />
  );
};
